<template>
  <div>
    <PricingFilterToolbar
      :search-term="searchTerm"
      :expired-filter="expiredFilter"
      search-placeholder="Search by Description or Label..."
      @search-change="searchTerm = $event"
      @expired-filter-change="expiredFilter = $event"
    >
      <template v-slot:actions>
        <button
          v-if="userEditRight"
          type="button"
          @click="
            pricePackage = deepCopyObject(emptyPackage);
            $refs.modifyPackageModal.open();
          "
          class="btn btn-info mr-3 mb-2"
        >
          Add New Package
        </button>
      </template>
    </PricingFilterToolbar>
    <div
      class="table-responsive table-bordered"
      v-if="packages.length > 0"
    >
      <table class="table pricing-table" ref="table">
        <thead>
          <tr class="text-nowrap">
            <th class="pricing-column-description">Description</th>
            <th class="pricing-column-label">Label</th>
            <th class="pricing-column-bandwidth">Bandwidth</th>
            <th class="pricing-column-price">NRC (excl. VAT)</th>
            <th class="pricing-column-price">MRC (excl. VAT)</th>
            <th class="pricing-column-price">MRC (incl. VAT)</th>
            <th class="pricing-column-status">Expired</th>
            <th class="pricing-column-id">#</th>
            <th class="pricing-column-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            :class="{ 'bg-primary': connectivityPackage.expired }"
            v-for="connectivityPackage in packages"
            :key="connectivityPackage.dataCentreInternetPackageNumber"
          >
            <td>
              {{ connectivityPackage.description }}
            </td>
            <td class="pricing-column-label">
              {{ connectivityPackage.label }}
            </td>
            <td class="wd-md">{{ speed_bitsToMbits(connectivityPackage.bandwidth) }} Mbit/s</td>
            <td class="text-nowrap">
              {{
                getSetupFee(
                  connectivityPackage.datacentreinternetpackageprices,
                  period
                )
              }}
            </td>
            <td>
              {{
                getPrice(connectivityPackage.datacentreinternetpackageprices, period)
              }}
              <span
                v-if="userEditRight"
                style="cursor: pointer"
                @click="openPriceModal(connectivityPackage)"
                v-tooltip="'Edit Row Price'"
                class="pull-right"
              >
                <em class="fa fa-edit fa-fw text-green"></em>
              </span>
            </td>
            <td class="text-nowrap">
              {{
                getPrice(
                  connectivityPackage.datacentreinternetpackageprices,
                  period,
                  true
                )
              }}
            </td>
            <td>
              <span
                class="badge"
                :class="
                  connectivityPackage.expired ? 'badge-warning' : 'badge-green'
                "
                >{{ connectivityPackage.expired }}
              </span>
            </td>
            <td>{{ connectivityPackage.dataCentreInternetPackageNumber }}</td>
            <td class="text-center">
              <button
                v-if="userEditRight"
                v-tooltip="'Edit Package'"
                @click="editPackage(connectivityPackage)"
                type="button"
                class="btn btn-sm btn-info m-1"
              >
                <em class="fa fa-edit fa-fw"></em>
              </button>
              <button
                v-if="userEditRight"
                v-tooltip="'Delete Package'"
                @click="deletePackage(connectivityPackage)"
                type="button"
                class="btn btn-sm btn-primary"
              >
                <em class="fa fa-trash fa-fw"></em>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="alert alert-info">No Prices Available to Display</div>
    <ModifyDataCentrePackageModal
      ref="modifyPackageModal"
      :pricePackage="pricePackage"
      :contractTerms="contractTerms"
      @packageAdded="packageAdded"
      @packageEdited="$emit('get-connectivity-packages')"
    />
    <AddPriceModal
      ref="addPriceModal"
      :row-price="rowPrice"
      :new-price="newPrice"
      :package-description="packageDescription"
      @addPrice="addPrice"
      @pricesAdded="$emit('get-connectivity-packages')"
    />
  </div>
</template>

<script>
import PriceFunctions from "@/components/Admin/PriceFunctions";
import ModifyDataCentrePackageModal from "@/components/Admin/Modals/ModifyDataCentrePackageModal";
import AddPriceModal from "@/components/Admin/Modals/AddPriceModal";
import PricingFilterToolbar from "@/components/Admin/PricingFilterToolbar";
export default {
  extends: PriceFunctions,
  props: {
    connectivityPackages: {
      type: Array,
      required: true,
    },
    productNumber: {
      type: Number,
      required: true,
    },
    emptyPackage: {
      type: Object,
      required: true,
    },
    userEditRight: {
      type: Boolean,
      required: true
    },

  },
  components: {
    ModifyDataCentrePackageModal,
    AddPriceModal,
    PricingFilterToolbar,
  },
  data() {
    return {
      period: this.getCurrentPeriod(),
      rowPrice: [],
      contractTerms: [],
      pricePackage: {},
      newPrice: {},
      packageDescription: '',
      expiredFilter: "false",
      searchTerm: "",
    };
  },
  computed: {
    packages() {
      let filteredPackages = this.connectivityPackages;

      if (this.expiredFilter === "false") {
        filteredPackages = filteredPackages.filter((pkg) => pkg.expired === false);
      } else if (this.expiredFilter === "true") {
        filteredPackages = filteredPackages.filter((pkg) => pkg.expired === true);
      }

      if (this.searchTerm) {
        const normalizedSearchTerm = this.searchTerm.toLowerCase();
        filteredPackages = filteredPackages.filter((pkg) =>
          [pkg.description, pkg.label, this.speed_bitsToMbits(pkg.bandwidth), pkg.bandwidth]
            .filter((value) => value !== null && value !== undefined && value !== "")
            .some((value) =>
              String(value).toLowerCase().includes(normalizedSearchTerm)
            )
        );
      }

      return filteredPackages;
    },
  },
  methods: {
    getLabel({ name, location }) {
      return name + " - " + location;
    },
    addNewPackage(serviceType) {
      this.pricePackage = this.deepCopyObject(this.emptyPackage);
      this.pricePackage.type = serviceType;
      this.$refs.modifyPackageModal.open();
    },
    editPackage(connectivityPackage) {
      this.pricePackage = this.deepCopyObject(connectivityPackage);
      this.$refs.modifyPackageModal.open();
    },
    packageAdded(connectivityPackage) {
      connectivityPackage.datacentreinternetpackageprices = [];
      this.connectivityPackages.push(connectivityPackage);
      this.openPriceModal(connectivityPackage);
    },
    setNewPrice() {
      this.newPrice = this.deepCopyObject(
        this.emptyPackage.datacentreinternetpackageprice
      );
    },
    openPriceModal(connectivityPackage) {
      this.setNewPrice();
      this.rowPrice = this.deepCopyObject(
        connectivityPackage.datacentreinternetpackageprices
      );
      this.packageDescription = connectivityPackage.description || '';
      this.newPrice.dataCentreInternetPackageNumber =
        connectivityPackage.dataCentreInternetPackageNumber;
      this.$refs.addPriceModal.open();
    },
    addPrice(newPrice) {
      this.rowPrice.forEach((price) => {
        if (price.validUntil == 0) {
          if (newPrice.validFrom % 100 > 1) {
            price.validUntil = newPrice.validFrom - 1;
          } else {
            price.validUntil = newPrice.validFrom - 100 + 11;
          }
        }
      });
      this.rowPrice.push(newPrice);
      this.setNewPrice();
      this.newPrice.dataCentreInternetPackageNumber =
        newPrice.dataCentreInternetPackageNumber;
    },
    deletePackage(connectivityPackage) {
      this.$swal({
        title: "Are you sure you want to delete this price package?",
        type: "warning",
        showCancelButton: true,
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.$http
            .delete(
              this.$config.aimsAPI +
                "pricing/pricepackages/" +
                connectivityPackage.objKey +
                "?objClassName=" +
                connectivityPackage.class
            )
            .then(
              (response) => {
                this.showSuccess(response.data.message);
                this.$emit("get-connectivity-packages");
              },
              (error) => {
                this.showError("Error Deleting Price Package", error);
              }
            );
        }
      });
    }
  },
};
</script>
